import React, {useEffect} from "react";
import {useHistory, useLocation} from "react-router-dom";
import {Button, styled, theme, Text} from "@reside/ui";
import {RoleNameEnum} from "../../utils/types/common";
import {TableUserDto} from "../../utils/types/user";
import {DocumentTitle} from "../../atoms/document-title";
import {AdminRoles} from "../../atoms/admin-roles";
import {Footer} from "../../atoms/footer";
import {ReportingLayout} from "./reporting-layout";
import {PERMISSIONS_MANAGEMENT_LIST_PATH} from "./atoms/permissions-user-detail";
import {
  BulkPermissionManagementForm,
  formatBulkPendingChangesLabel,
  PermissionsBulkEditHeader,
  useBulkUserPermissionsMutation,
} from "./atoms/permissions-bulk-edit";

type LocationState = Readonly<{
  users?: ReadonlyArray<TableUserDto>;
}>;

const PageReportingPermissionsBulkEditRenderer = () => {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const users = location.state?.users ?? [];

  useEffect(() => {
    if (users.length === 0) {
      history.replace(PERMISSIONS_MANAGEMENT_LIST_PATH);
    }
  }, [history, users.length]);

  const {
    discardChanges,
    hasPermission,
    isPermissionPending,
    isSaving,
    pendingChangeCount,
    save,
    toggleCategory,
    togglePermission,
    userIds,
  } = useBulkUserPermissionsMutation(users);

  const goBackToList = () => {
    history.push(PERMISSIONS_MANAGEMENT_LIST_PATH);
  };

  const handleCancel = () => {
    discardChanges();
    goBackToList();
  };

  if (users.length === 0) {
    return null;
  }

  return (
    <ReportingLayout isActionRowVisible={false}>
      <DocumentTitle title="Reside - Admin - Bulk Permissions" />
      <AdminRoles
        roles={[RoleNameEnum.RESIDE_ADMIN, RoleNameEnum.ORGANIZATION_ADMIN]}
      >
        <PageContainer>
          <PermissionsBulkEditHeader users={users} />
          <FormSection>
            <BulkPermissionManagementForm
              userIds={userIds}
              hasPermission={hasPermission}
              isPermissionPending={isPermissionPending}
              onTogglePermission={togglePermission}
              onToggleCategory={toggleCategory}
            />
          </FormSection>
          <StickyFooter
            left={
              pendingChangeCount > 0 ? (
                <PendingChangesText>
                  {formatBulkPendingChangesLabel(
                    pendingChangeCount,
                    userIds.length,
                  )}
                </PendingChangesText>
              ) : null
            }
            right={
              <FooterButtons>
                <Button outline color="primary" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  color="primary"
                  disabled={isSaving || pendingChangeCount === 0}
                  onClick={() => save()}
                >
                  Save
                </Button>
              </FooterButtons>
            }
          />
        </PageContainer>
      </AdminRoles>
    </ReportingLayout>
  );
};

export const PageReportingPermissionsBulkEdit =
  PageReportingPermissionsBulkEditRenderer;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 8px 0 0;
`;

const FormSection = styled.div`
  flex: 1;
  padding-bottom: 40px;
`;

const StickyFooter = styled(Footer)`
  align-items: center;
  background-color: #ffffff;
  box-shadow: 0 -12px 12px 0 ${theme.color.white},
    0 -12px 24px 0 ${theme.color.white};
  margin-top: 32px;
  padding: 16px 0;
  width: 100%;
  z-index: 2;

  @media (min-width: ${({theme}) => theme.breakpoint.md}px) {
    padding: 24px 0;
  }
`;

const PendingChangesText = styled(Text)`
  color: ${theme.color.darkBlue100};
  font-size: 15px;
  line-height: 24px;
  margin-left: 24px;
`;

const FooterButtons = styled.div`
  display: flex;
  gap: 12px;
  padding-right: 80px;
`;
